/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EMAIL, PHONE_NUMBER } from "@utils/expreciones";
import { RootState } from "@store/store";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";
import CenteredLoader from "@components/CenteredLoader";
import { useLazyGetReferencesQuery, usePatchReferencesMutation, usePostReferencesMutation } from "@api/referenciasApi";
import { useLazyGetReferenceTypesQuery, useLazyGetTaxSystemsQuery } from "@api/catalogs";
import { usePutApplicationCompletedMutation } from "@api/applications";
import { InactivityHandler } from "@components/InactivityHandler";
import {
  buildProductRoute,
  createEmptyReference,
  getFormikArrayError,
  getOrdinalText,
  getReferenceConfig,
  getReferenceIndex,
} from "@helpers/funciones";
import { REFERENCE_DEAFUALT_VALUES, REFERENCES_CODES } from "@helpers/constantes";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError, ReferenceType, ReferenceSupplierError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import useReferenciasForm from "@hooks/useReferenciasForm";
import { UUID } from "crypto";
import * as Sentry from "@sentry/react";

const ReferenciasPersonales = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isVisible, isRequired, isConfigLoading } = useReferenciasForm("clientes");

  const applicationData = useSelector((state: RootState) => state.register.application);
  const client = useSelector((state: RootState) => state.register.personal_data);

  const [triggerPostReferences] = usePostReferencesMutation();
  const [triggerPatchReferences] = usePatchReferencesMutation();
  const [putAapplicationCompleted] = usePutApplicationCompletedMutation();
  const [getReferenceTypes] = useLazyGetReferenceTypesQuery();
  const [getReferences] = useLazyGetReferencesQuery();
  const [getTaxSystems] = useLazyGetTaxSystemsQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [referenceType, setReferenceType] = useState<ReferenceType | null>(null);
  const [minReference, setMinReference] = useState(REFERENCE_DEAFUALT_VALUES.min);
  const [requiredReference, setRequiredReference] = useState(REFERENCE_DEAFUALT_VALUES.required);

  const formik = useFormik({
    initialValues: {
      referencia: Array.from({ length: REFERENCE_DEAFUALT_VALUES.required }, () => ({
        ...createEmptyReference("proveedor"),
        relacion: "",
        direccion: "",
      })),
    },
    validationSchema: yup.object().shape({
      referencia: yup.array().of(
        yup.object().shape({
          country_code: yup.string().test("required-if-min", "Campo requerido", function (value, params) {
            if (!isRequired("phone_number", true)) return true;
            return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
          }),
          phone: yup
            .string()
            .test("required-if-min", "Campo requerido", function (value, params) {
              if (!isRequired("phone_number", true)) return true;
              return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
            })
            .matches(PHONE_NUMBER, { message: "Número de teléfono inválido", excludeEmptyString: true })
            .test("unique", "El teléfono ya se encuentra registrado.", function (value, params) {
              if (!value || !isVisible("phone_number")) return true;
              const reference_index = getReferenceIndex(params.path);
              const reference_phone =
                (formik.values.referencia[reference_index].country_code || "") +
                (formik.values.referencia[reference_index].phone || "") +
                (formik.values.referencia[reference_index].phone_extension || "");
              const array_items = formik.values.referencia.map((item) => {
                return (item.country_code || "") + (item.phone || "") + (item.phone_extension || "");
              });

              const num_results: number = array_items.filter((item) => item === reference_phone).length;
              return num_results > 1 ? false : true;
            }),
          contact_name: yup.string().test("required-if-min", "Campo requerido", function (value, params) {
            if (!isRequired("contact_name", true)) return true;
            return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
          }),
          email: yup
            .string()
            .test("required-if-min", "Campo requerido", function (value, params) {
              if (!isRequired("email", true)) return true;
              return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
            })
            .matches(EMAIL, {
              message: "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)",
              excludeEmptyString: true,
            })
            .test("unique", "El email ya se encuentra registrado.", function (value) {
              if (!value || !isVisible("email")) return true;
              const array_items = formik.values.referencia.map((item) => item.email || "");
              const num_results: number = array_items.filter((item) => item === value).length;
              return num_results > 1 ? false : true;
            }),
          relacion: yup.string().test("required-if-min", "Campo requerido", function (value, params) {
            if (!isRequired("relationship_type", false)) return true;
            return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
          }),
          direccion: yup.string().test("required-if-min", "Campo requerido", function (value, params) {
            if (!isRequired("address", false)) return true;
            return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
          }),
        }),
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!formik.isValid || !referenceType) {
          return;
        }

        setSubmitting(true);

        for (let index = 0; index < values.referencia.length; index++) {
          const reference = values.referencia[index];
          if (isReferenceEmpty(reference)) continue;

          const referenceData = {
            client_details_id: client.id,
            id_ext: 4444,
            phone_number: reference.phone,
            reference_type_id: referenceType.id,
            code_phone_number: reference.country_code,
            phone_extension: reference.phone_extension,
            supplier_name: "",
            supplier_rfc: "",
            contact_name: reference.contact_name,
            email: reference.email,
            percentage_share: 0,
            relationship_type: reference.relacion,
            address: reference.direccion,
          };

          if (reference.id) {
            await triggerPatchReferences({
              referenceId: reference.id as UUID,
              body: referenceData,
            }).unwrap();
          } else {
            await triggerPostReferences(referenceData).unwrap();
          }
        }

        await putAapplicationCompleted({
          id_sol: applicationData.id,
          body: {
            references_status: "COMPLETO",
          },
        }).unwrap();

        navigate(buildProductRoute(location.pathname, "expediente_pf"));
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "ReferenciasPersonales-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const isReferenceEmpty = (reference: (typeof formik.values.referencia)[number]) =>
    !(isVisible("contact_name") && reference.contact_name) &&
    !(isVisible("email") && reference.email) &&
    !(isVisible("phone_number") && reference.phone) &&
    !(isVisible("relationship_type") && reference.relacion) &&
    !(isVisible("address") && reference.direccion);

  const saveReference = async (
    e:
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | Element>
      | React.FocusEvent<HTMLInputElement, Element>
      | React.SyntheticEvent<Element, Event>,
    index: number,
  ) => {
    try {
      formik.handleBlur(e);

      if (!referenceType) return;

      const reference = formik.values.referencia[index];
      if (isReferenceEmpty(reference)) return;

      if (formik.errors.referencia?.[index]) {
        const rowErrors = formik.errors.referencia[index] as unknown as ReferenceSupplierError & {
          relacion?: string;
          direccion?: string;
        };
        const {
          contact_name: contact_name_err,
          email: email_err,
          country_code: country_code_err,
          phone: phone_err,
          phone_extension: phone_extension_err,
          relacion: relacion_err,
          direccion: direccion_err,
        } = rowErrors;

        if (
          contact_name_err ||
          email_err ||
          country_code_err ||
          phone_err ||
          phone_extension_err ||
          relacion_err ||
          direccion_err
        ) {
          return;
        }
      }

      const referenceData = {
        client_details_id: client.id,
        id_ext: 4444,
        phone_number: reference.phone,
        reference_type_id: referenceType.id,
        code_phone_number: reference.country_code,
        phone_extension: reference.phone_extension,
        supplier_name: "",
        supplier_rfc: "",
        contact_name: reference.contact_name,
        email: reference.email,
        percentage_share: 0,
        relationship_type: reference.relacion,
        address: reference.direccion,
      };

      if (reference.id) {
        await triggerPatchReferences({
          referenceId: reference.id as UUID,
          body: referenceData,
        }).unwrap();
      } else {
        const newReference = await triggerPostReferences(referenceData).unwrap();
        formik.setFieldValue(`referencia[${index}].id`, newReference.data.id);
      }
    } catch (error) {
      console.error("Error guardando referencia:", error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        if (!client.id) return;
        const [reference_types, tax_systems] = await Promise.all([
          getReferenceTypes().unwrap(),
          getTaxSystems(applicationData.type).unwrap(),
        ]);

        const references_config = tax_systems?.config_extra?.references_config?.[REFERENCES_CODES.personal];
        const { total_items: required, min_items: min } = getReferenceConfig(references_config);

        setRequiredReference(required);
        setMinReference(min);

        const reference_type_personal = reference_types.find((item) => item.code === REFERENCES_CODES.personal);
        if (!reference_type_personal) return;

        setReferenceType(reference_type_personal);

        const references_result = await getReferences({
          clientDetailsId: client.id,
          referenceTypeId: reference_type_personal.id,
        }).unwrap();

        const values = references_result
          .filter((item) => item.reference_type_id === reference_type_personal.id)
          .slice(0, required)
          .map((item) => {
            return {
              id: item.id,
              country_code: item.code_phone_number,
              phone: item.phone_number,
              contact_name: item.contact_name,
              proveedor: item.supplier_rfc,
              email: item.email,
              phone_extension: item.phone_extension,
              share: item.share,
              relacion: item.relationship_type || "",
              direccion: item.address || "",
            };
          });

        for (let index = values.length; index < required; index++) {
          values.push({ ...createEmptyReference("proveedor"), relacion: "", direccion: "" });
        }

        formik.setFieldValue("referencia", values);

        setTimeout(() => {
          formik.validateForm();
        }, 200);

        window.scrollTo(0, 0);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "ReferenciasPersonales-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        {isConfigLoading || isLoading ? (
          <CenteredLoader />
        ) : (
          <Box maxWidth="sm" mx="auto" mt={4} px={2}>
            <Typography
              textAlign={"center"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={700}
              color={theme?.palette?.primary?.dark}
            >
              Referencias
            </Typography>

            <Typography
              textAlign={"center"}
              color={theme?.palette?.primary?.main}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              mt={2}
            >
              Ahora, escribe {requiredReference} referencias personales, considera que podríamos contactarlos. Debes
              completar al menos {minReference}.
            </Typography>

            <Container maxWidth="sm" sx={{ mt: 2, mb: 2 }}>
              <form onSubmit={formik.handleSubmit} noValidate>
                {formik.values.referencia.map((_, index) => (
                  <div key={index}>
                    <Box mt={4}>
                      <Typography
                        textAlign={"left"}
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        fontWeight={400}
                        color={theme?.palette?.primary?.dark}
                      >
                        {getOrdinalText(index + 1)} referencia
                      </Typography>
                    </Box>

                    {isVisible("contact_name") && (
                      <FormControl fullWidth>
                        <TextField
                          name={`referencia[${index}].contact_name`}
                          id={`contact_name${index}`}
                          label="Nombre completo"
                          margin="normal"
                          required={index < minReference && isRequired("contact_name", true)}
                          fullWidth
                          autoComplete="off"
                          onChange={formik.handleChange}
                          onBlur={(e) => saveReference(e, index)}
                          error={Boolean(getFormikArrayError(formik, "referencia", index, "contact_name"))}
                          helperText={getFormikArrayError(formik, "referencia", index, "contact_name")}
                          value={formik.values.referencia?.[index]?.contact_name}
                          disabled={formik.isSubmitting || isLoading}
                        />
                      </FormControl>
                    )}

                    {isVisible("email") && (
                      <TextField
                        name={`referencia[${index}].email`}
                        id={`email${index}`}
                        label="Correo electrónico"
                        margin="normal"
                        required={index < minReference && isRequired("email", true)}
                        fullWidth
                        autoComplete="off"
                        onChange={(e) => {
                          const value_to_set = (e.target.value || "").toLowerCase();
                          formik.setFieldValue(`referencia[${index}].email`, value_to_set);
                        }}
                        onBlur={(e) => saveReference(e, index)}
                        error={Boolean(getFormikArrayError(formik, "referencia", index, "email"))}
                        helperText={getFormikArrayError(formik, "referencia", index, "email")}
                        value={formik.values.referencia?.[index]?.email}
                        disabled={formik.isSubmitting}
                        inputProps={{ maxLength: 100 }}
                      />
                    )}

                    {isVisible("phone_number") && (
                      <>
                        <Grid container spacing={1} mt={1}>
                          <Grid item xs={4} sm={4} md={4}>
                            <CustomContryCode
                              label="Código"
                              name={`referencia[${index}].country_code`}
                              value={formik.values.referencia?.[index]?.country_code}
                              onChange={formik.handleChange}
                              onBlur={(e) => saveReference(e, index)}
                              error={Boolean(getFormikArrayError(formik, "referencia", index, "country_code"))}
                              disabled={formik.isSubmitting || isLoading}
                            />
                          </Grid>

                          <Grid item xs={8} sm={8} md={8}>
                            <TextField
                              name={`referencia[${index}].phone`}
                              id={`phone${index}`}
                              type="text"
                              variant="outlined"
                              label="Teléfono"
                              fullWidth
                              required={index < minReference && isRequired("phone_number", true)}
                              value={formik.values.referencia?.[index]?.phone}
                              onChange={(event) => {
                                formik.setFieldValue(
                                  `referencia[${index}].phone`,
                                  event.target.value.replace(/[^0-9]/g, ""),
                                );
                              }}
                              onBlur={(e) => saveReference(e, index)}
                              error={Boolean(getFormikArrayError(formik, "referencia", index, "phone"))}
                              helperText={getFormikArrayError(formik, "referencia", index, "phone")}
                              inputProps={{
                                maxLength: 10,
                                inputMode: "numeric",
                              }}
                              disabled={formik.isSubmitting || isLoading}
                            />
                          </Grid>
                        </Grid>

                        <TextField
                          name={`referencia[${index}].phone_extension`}
                          id={`phone_extension${index}`}
                          label="Extensión del teléfono(si aplica)"
                          margin="normal"
                          fullWidth
                          autoComplete="off"
                          onChange={(e) => {
                            const value_to_set = (e.target.value || "").replace(/[^0-9]/g, "");
                            formik.setFieldValue(`referencia[${index}].phone_extension`, value_to_set);
                          }}
                          onBlur={(e) => saveReference(e, index)}
                          error={Boolean(getFormikArrayError(formik, "referencia", index, "phone_extension"))}
                          helperText={getFormikArrayError(formik, "referencia", index, "phone_extension")}
                          value={formik.values.referencia?.[index]?.phone_extension}
                          disabled={formik.isSubmitting}
                          inputProps={{
                            maxLength: 5,
                            inputMode: "numeric",
                          }}
                        />
                      </>
                    )}

                    {isVisible("relationship_type") && (
                      <TextField
                        name={`referencia[${index}].relacion`}
                        id={`relacion${index}`}
                        label="Tipo de relación"
                        margin="normal"
                        required={index < minReference && isRequired("relationship_type", false)}
                        fullWidth
                        autoComplete="off"
                        onChange={formik.handleChange}
                        onBlur={(e) => saveReference(e, index)}
                        error={Boolean(getFormikArrayError(formik, "referencia", index, "relacion"))}
                        helperText={getFormikArrayError(formik, "referencia", index, "relacion")}
                        value={formik.values.referencia?.[index]?.relacion}
                        disabled={formik.isSubmitting || isLoading}
                        inputProps={{ maxLength: 100 }}
                      />
                    )}

                    {isVisible("address") && (
                      <TextField
                        name={`referencia[${index}].direccion`}
                        id={`direccion${index}`}
                        label="Dirección"
                        margin="normal"
                        required={index < minReference && isRequired("address", false)}
                        fullWidth
                        autoComplete="off"
                        onChange={formik.handleChange}
                        onBlur={(e) => saveReference(e, index)}
                        error={Boolean(getFormikArrayError(formik, "referencia", index, "direccion"))}
                        helperText={getFormikArrayError(formik, "referencia", index, "direccion")}
                        value={formik.values.referencia?.[index]?.direccion}
                        disabled={formik.isSubmitting || isLoading}
                        inputProps={{ maxLength: 200 }}
                      />
                    )}
                  </div>
                ))}
                <Grid container spacing={1} my={2}>
                  <Grid item xs={6} sm={6} md={6}>
                    <Button
                      variant="blue_outlined"
                      type="button"
                      fullWidth
                      sx={{ padding: "0px", height: "100%" }}
                      onClick={() => {
                        navigate(buildProductRoute(location.pathname, "expediente_pf"));
                      }}
                      disabled={formik.isSubmitting || isLoading}
                    >
                      Regresar al inicio
                    </Button>
                  </Grid>

                  <Grid item xs={6} sm={6} md={6}>
                    <Button
                      variant="default"
                      type="submit"
                      fullWidth
                      disabled={!formik.isValid || formik.isSubmitting || isLoading}
                    >
                      Continuar
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Container>
          </Box>
        )}
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default ReferenciasPersonales;
